import React, { useState } from 'react';
import { Table, Button, Space, Tag, message, Modal, Form, InputNumber, Input, Card, Statistic } from 'antd';
import { PlusOutlined, EyeOutlined, GiftOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, addUserBonus, getUserBalanceHistory } from './api';

const { TextArea } = Input;

interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  balance: number;
  currency: string;
}

interface UserBalanceTableProps {}

const UserBalanceTable: React.FC<UserBalanceTableProps> = () => {
  const queryClient = useQueryClient();
  const [isBonusModalVisible, setIsBonusModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const { data: balanceHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['userBalanceHistory', selectedUser?.id],
    queryFn: () => selectedUser ? getUserBalanceHistory(selectedUser.id) : null,
    enabled: !!selectedUser && isHistoryModalVisible,
  });

  const addBonusMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: any }) => addUserBonus(userId, data),
    onSuccess: () => {
      message.success('Бонус успешно добавлен');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsBonusModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при добавлении бонуса');
    },
  });

  const handleAddBonus = (user: User) => {
    setSelectedUser(user);
    form.resetFields();
    setIsBonusModalVisible(true);
  };

  const handleViewHistory = (user: User) => {
    setSelectedUser(user);
    setIsHistoryModalVisible(true);
  };

  const handleBonusModalOk = () => {
    form.validateFields().then((values) => {
      if (selectedUser) {
        addBonusMutation.mutate({
          userId: selectedUser.id,
          data: values,
        });
      }
    });
  };

  const handleBonusModalCancel = () => {
    setIsBonusModalVisible(false);
    setSelectedUser(null);
    form.resetFields();
  };

  const handleHistoryModalCancel = () => {
    setIsHistoryModalVisible(false);
    setSelectedUser(null);
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 1000) return 'green';
    if (balance > 100) return 'blue';
    if (balance > 0) return 'orange';
    return 'red';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Пользователь',
      key: 'user',
      render: (record: User) => (
        <div>
          <div className="font-medium text-gray-900">
            {record.firstName && record.lastName 
              ? `${record.firstName} ${record.lastName}`
              : record.email
            }
          </div>
          <div className="text-sm text-gray-500">{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Баланс',
      key: 'balance',
      render: (record: User) => (
        <Tag color={getBalanceColor(record.balance)}>
          <span className="font-medium">
            {record.balance} {record.currency}
          </span>
        </Tag>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (record: User) => (
        <Space size="small">
          <Button
            type="text"
            icon={<GiftOutlined />}
            onClick={() => handleAddBonus(record)}
            title="Добавить бонус"
          />
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewHistory(record)}
            title="История операций"
          />
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeConfig = {
          deposit: { text: 'Пополнение', color: 'green' },
          purchase: { text: 'Покупка', color: 'red' },
          bonus: { text: 'Бонус', color: 'blue' },
          refund: { text: 'Возврат', color: 'orange' },
          referral: { text: 'Реферальный', color: 'purple' },
        };
        const config = typeConfig[type as keyof typeof typeConfig] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Сумма',
      key: 'amount',
      render: (record: any) => (
        <span className={record.amount > 0 ? 'text-green-600' : 'text-red-600'}>
          {record.amount > 0 ? '+' : ''}{record.amount} {record.currency}
        </span>
      ),
    },
    {
      title: 'Баланс до',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      render: (balance: number, record: any) => `${balance} ${record.currency}`,
    },
    {
      title: 'Баланс после',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number, record: any) => `${balance} ${record.currency}`,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <div className="max-w-xs truncate" title={description}>
          {description}
        </div>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('ru-RU'),
    },
  ];

  const totalBalance = users?.reduce((sum: number, user: User) => sum + user.balance, 0) || 0;
  const totalUsers = users?.length || 0;
  const activeUsers = users?.filter((user: User) => user.balance > 0).length || 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Балансы пользователей</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <Statistic
              title="Общий баланс"
              value={totalBalance}
              suffix="TJS"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
          <Card>
            <Statistic
              title="Всего пользователей"
              value={totalUsers}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
          <Card>
            <Statistic
              title="Пользователей с балансом"
              value={activeUsers}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} пользователей`,
        }}
      />

      {/* Модальное окно для добавления бонуса */}
      <Modal
        title={`Добавить бонус пользователю: ${selectedUser?.firstName || selectedUser?.email}`}
        open={isBonusModalVisible}
        onOk={handleBonusModalOk}
        onCancel={handleBonusModalCancel}
        confirmLoading={addBonusMutation.isPending}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="amount"
            label="Сумма бонуса"
            rules={[{ required: true, message: 'Введите сумму бонуса' }]}
          >
            <InputNumber
              placeholder="Введите сумму"
              style={{ width: '100%' }}
              min={0.01}
              step={0.01}
              addonAfter="TJS"
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Причина"
            rules={[{ required: true, message: 'Введите причину бонуса' }]}
          >
            <TextArea
              placeholder="Например: Реферальный бонус, Бонус за активность, и т.д."
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для просмотра истории */}
      <Modal
        title={`История операций: ${selectedUser?.firstName || selectedUser?.email}`}
        open={isHistoryModalVisible}
        onCancel={handleHistoryModalCancel}
        footer={null}
        width={800}
      >
        <Table
          columns={historyColumns}
          dataSource={balanceHistory?.transactions || []}
          rowKey="id"
          loading={historyLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} операций`,
          }}
        />
      </Modal>
    </div>
  );
};

export default UserBalanceTable;
